import StepUserType from 'components/GetStarted/StepUserType'
import StepContactInfo from 'components/GetStarted/StepContactInfo'
import StepEnterName from 'components/GetStarted/StepEnterName'
import StepUserCreate from 'components/GetStarted/StepUserCreate'
import StepUserLocation from 'components/GetStarted/StepUserLocation'
import StepSponsorship from 'components/GetStarted/StepSponsorship'
import StepServices from 'components/GetStarted/StepServices'
import StepEnterRate from 'components/GetStarted/StepEnterRate'
import StepEnterDetails from 'components/GetStarted/StepEnterDetails'
import StepSelectPlan from 'components/GetStarted/StepSelectPlan'
import StepThankYou from 'components/GetStarted/StepThankYou'
import StepPayment from 'components/GetStarted/StepPayment'
import {
  isSponsorshipDisabled,
  hasSelectedBasicPlan,
  isContactInfoPrevStepIncomplete,
  isEnterNamePrevStepIncomplete,
  isCreateUserPrevStepIncomplete,
  isSponsorshipPrevStepIncomplete,
  isAnonymousUser,
  isRatePrevStepIncomplete,
  isDetailsPrevStepIncomplete,
  isPaymentPrevStepIncomplete,
  isServicesPrevStepIncomplete,
  hasUserTypeInUrl,
  hasEmptyServices,
  hasEmptyStripePlans,
  isSubscribed,
  multipleListingsDisabled,
  hasActiveListing,
  multipleListingsEnabled,
  hasNoActiveListing,
  isLoggedIn,
} from 'utils/getStartedUtils'
import { GET_STARTED_ROUTES } from './constants'

const subscribedRedirect = (data) => data?.viewer?.user?.directory_path

const REDIRECT_TESTS = {
  USER_CREATION: [
    {
      test: [isSubscribed, hasActiveListing, multipleListingsDisabled],
      redirectTo: subscribedRedirect,
    },
    {
      test: [isSubscribed, multipleListingsEnabled],
      redirectTo: GET_STARTED_ROUTES.LOCATION,
    },
    { test: [isLoggedIn, hasActiveListing], redirectTo: GET_STARTED_ROUTES.PLANS },
    { test: [isLoggedIn, hasNoActiveListing], redirectTo: GET_STARTED_ROUTES.LOCATION },
  ],
  LISTING_CREATION: [
    { test: [isAnonymousUser], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
    {
      test: [isLoggedIn, hasActiveListing, multipleListingsDisabled],
      redirectTo: GET_STARTED_ROUTES.PLANS,
    },
  ],
  SUBSCRIPTION: [
    { test: [isAnonymousUser], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
    { test: [isSubscribed], redirectTo: subscribedRedirect },
    { test: [hasNoActiveListing], redirectTo: GET_STARTED_ROUTES.LOCATION },
  ],
}

/**
 * This config is what drives the get started flow's (GSF) step progression.
 * Each object in the steps array represents a step in GSF and the steps are
 * ordered in the array according to the GSF sequence.
 *
 * step object:
 * {
 *    component: <name of component that will be rendered for step>
 *    urlSlug: <url slug for this step>
 *    skipIf: <array of predicates - if any evaluate to true step will be skipped>
 *    redirectIf: <array of test objects - all redirect tests need to pass before step renders>
 * }
 *
 * test object:
 * {
 *    test: <array of predicates - if all pass then redirect will occur>
 *    redirectTo: <string or function in format (data) => return redirect url>
 * }
 */
const config = {
  steps: [
    {
      component: StepUserType,
      urlSlug: GET_STARTED_ROUTES.USER_TYPE,
      redirectIf: [
        ...REDIRECT_TESTS.USER_CREATION,
        { test: [hasUserTypeInUrl], redirectTo: GET_STARTED_ROUTES.CONTACT },
      ],
    },
    {
      component: StepContactInfo,
      urlSlug: GET_STARTED_ROUTES.CONTACT,
      redirectIf: [
        ...REDIRECT_TESTS.USER_CREATION,
        { test: [isContactInfoPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
      ],
    },
    {
      component: StepEnterName,
      urlSlug: GET_STARTED_ROUTES.FULLNAME,
      redirectIf: [
        ...REDIRECT_TESTS.USER_CREATION,
        { test: [isEnterNamePrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
      ],
    },
    {
      component: StepUserCreate,
      urlSlug: GET_STARTED_ROUTES.PASSWORD,
      redirectIf: [
        ...REDIRECT_TESTS.USER_CREATION,
        { test: [isCreateUserPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
      ],
    },
    {
      component: StepUserLocation,
      urlSlug: GET_STARTED_ROUTES.LOCATION,
      redirectIf: REDIRECT_TESTS.LISTING_CREATION,
    },
    {
      component: StepSponsorship,
      urlSlug: GET_STARTED_ROUTES.SPONSORSHIP,
      skipIf: [isSponsorshipDisabled],
      redirectIf: [
        ...REDIRECT_TESTS.LISTING_CREATION,
        { test: [isSponsorshipPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.LOCATION },
      ],
    },
    {
      component: StepServices,
      urlSlug: GET_STARTED_ROUTES.SERVICES,
      skipIf: [hasEmptyServices],
      redirectIf: [
        ...REDIRECT_TESTS.LISTING_CREATION,
        { test: [isServicesPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.LOCATION },
      ],
    },
    {
      component: StepEnterRate,
      urlSlug: GET_STARTED_ROUTES.RATE,
      redirectIf: [
        ...REDIRECT_TESTS.LISTING_CREATION,
        { test: [isRatePrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.LOCATION },
      ],
    },
    {
      component: StepEnterDetails,
      urlSlug: GET_STARTED_ROUTES.DETAILS,
      redirectIf: [
        ...REDIRECT_TESTS.LISTING_CREATION,
        { test: [isDetailsPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.LOCATION },
      ],
    },
    {
      component: StepSelectPlan,
      urlSlug: GET_STARTED_ROUTES.PLANS,
      skipIf: [hasEmptyStripePlans],
      redirectIf: REDIRECT_TESTS.SUBSCRIPTION,
    },
    {
      component: StepPayment,
      urlSlug: GET_STARTED_ROUTES.PAYMENT,
      skipIf: [hasSelectedBasicPlan, hasEmptyStripePlans],
      redirectIf: [
        ...REDIRECT_TESTS.SUBSCRIPTION,
        { test: [isPaymentPrevStepIncomplete], redirectTo: GET_STARTED_ROUTES.PLANS },
      ],
    },
    {
      component: StepThankYou,
      urlSlug: GET_STARTED_ROUTES.THANKYOU,
      redirectIf: [
        { test: [isAnonymousUser], redirectTo: GET_STARTED_ROUTES.USER_TYPE },
        { test: [hasNoActiveListing], redirectTo: GET_STARTED_ROUTES.LOCATION },
      ],
    },
  ],
}

export default config
